import React, { FC, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';

import { CarouselRight, CarouselRightSmall, Envelope, Present } from '@rover/icons';
import { Box, Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import { useStatsigClient } from '@rover/rsdk/src/modules/Statsig';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

const HEIGHT = 70;

type FullWidthLinkProps = {
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
};

const FullWidthLink: FC<FullWidthLinkProps> = ({ href, onClick, children }) => (
  // We can't use a Kibble Link here due to the custom styles needed
  <Flex
    as="a"
    href={href}
    onClick={onClick}
    alignItems="center"
    width="100%"
    height={HEIGHT}
    px="6x"
    gap="4x"
  >
    {children}
  </Flex>
);

const MobileReferLink: FC = () => {
  const theme = useTheme();

  const { client: statsigClient } = useStatsigClient();
  const isLoadingStatsig = statsigClient.loadingStatus === 'Loading';
  const rolloutAmbassadorOwnerEntryPointsUplifts = statsigClient.checkGate(
    'rollout_ambassador_owner_entry_points_uplifts'
  );

  const referralCTA = 'ssr-owner-mobile-web-referral';

  const handleIsVisible = useCallback(() => {
    fireDataLayerEvent({
      event: 'referral-cta-view',
      referral_cta: referralCTA,
    });
  }, []);

  const ref = useOnIntersect(handleIsVisible, true, { threshold: 0.5 });

  const { current: cc } = useCountryConfigurationShim();
  const formattedReferralGiftcardAmount = cc?.formattedReferralGiftcardAmount ?? '$20';
  const formattedReferralOwnerToOwnerReward = cc?.formattedReferralOwnerToOwnerReward ?? '$20';

  const fireClickEvent = (): void => {
    fireDataLayerEvent({
      event: 'referral-cta-click',
      referral_cta: referralCTA,
    });
  };

  // Returns the template for the ambassador owner entry points uplift referral link
  const AmbassadorOwnerEntryPointsUpliftTemplate = (
    <FullWidthLink href="/refer/?source=ssr-owner-mobile-web-referral" onClick={fireClickEvent}>
      <Flex flexDirection="row" alignItems="center" width="100%" justifyContent="space-between">
        <Flex alignItems="center" flexDirection="row" gap="2x">
          <Envelope
            width={theme.space['6x']}
            height={theme.space['6x']}
            style={{
              flexShrink: '0',
              marginTop: theme.space['-1x'],
              fill: theme.textColor.navigation.toString(),
            }}
            alt={t`Envelope icon`}
          />
          <Flex flexDirection="column">
            <Text
              textColor="navigation_hover"
              size="200"
              fontWeight="semibold"
              sx={{ flexGrow: 99 }}
            >
              {t`Invite a friend to Rover`}
            </Text>
            <Text textColor="navigation" size="100" sx={{ flexGrow: 99 }}>
              {t`Gift a ${formattedReferralGiftcardAmount} credit, earn a ${formattedReferralOwnerToOwnerReward} credit`}
            </Text>
          </Flex>
        </Flex>
        <CarouselRightSmall
          width={theme.space['6x']}
          height={theme.space['6x']}
          fill={theme.textColor.navigation.toString()}
        />
      </Flex>
    </FullWidthLink>
  );

  const DefaultReferralTemplate = (
    <FullWidthLink href="/refer/?source=ssr-owner-mobile-web-referral" onClick={fireClickEvent}>
      <Flex alignItems="center" width="100%" gap="2x" justifyContent="space-between">
        <Present
          width="30px"
          height="34px"
          style={{ flexShrink: '0', marginTop: '-4px' }}
          alt={t`Gift box icon`}
        />
        <Text size="200" fontWeight="semibold" sx={{ flexGrow: 99 }}>
          <Trans>
            Give {formattedReferralGiftcardAmount}, Get {formattedReferralOwnerToOwnerReward}
          </Trans>
        </Text>
        <CarouselRight
          width="28px"
          height="28px"
          style={{ flexShrink: '0' }}
          fill={theme.colors.green['500'].toString}
        />
      </Flex>
    </FullWidthLink>
  );

  if (cc?.supportsReferral !== true || isLoadingStatsig) {
    return null;
  }

  const color = rolloutAmbassadorOwnerEntryPointsUplifts
    ? theme.textColor.navigation
    : theme.colors.green['500'];

  const colorHover = rolloutAmbassadorOwnerEntryPointsUplifts
    ? theme.textColor.navigation_hover
    : theme.colors.green['500'];

  return (
    <Box
      borderTop={`1px solid ${theme.colors.border.primary}`}
      ref={ref}
      sx={{
        '& > *': {
          color: color.toString(),
          fill: color.toString(),
          textDecoration: 'none',
        },
        ':hover > *': {
          color: colorHover.toString(),
          fill: colorHover.toString(),
          textDecoration: 'underline',
        },
      }}
    >
      {rolloutAmbassadorOwnerEntryPointsUplifts
        ? AmbassadorOwnerEntryPointsUpliftTemplate
        : DefaultReferralTemplate}
    </Box>
  );
};

export default MobileReferLink;
